import type { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { CurationStatusBadge } from '@components/CurationStatusBadge';
import { TableStatus } from '@components/DataTable';
import type { DataTableRecord } from '@components/DataTable/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Dialog from '@components/Dialog/Dialog';
import NullStateDiv from '@components/NullStateDiv';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import * as ROUTES from '@constants/routes';

import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import { TableContainer } from '@layouts/Layouts/TableLayout';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

import LabResultsDrilldown from '@views/Records/LabResults/Drilldown';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  noRecords: {
    id: 'labResultsMain.noRecords',
    defaultMessage: 'Known Lab Results',
  },
});

const LabResultListView: React.FC<
  RouteComponentProps & {
    data: DiagnosticReportRecord[];
    tableData: DataTableRecord[];
  }
> = ({ data, tableData }) => {
  const { formatMessage } = useIntl();
  const {
    state: { isAdmin, isAuthenticated, isCurator, isPractitioner, isVitall },
  } = useSession();
  const { navigateRoute } = useNavigate();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [labInfo, setLabInfo] = useState<{
    category?: string;
    title: string;
    patient?: string;
  }>({
    category: undefined,
    title: '',
    patient: undefined,
  });
  const handleOpenDialog = ({
    title,
    patient,
    category,
  }: {
    title: string;
    patient: string;
    category?: string;
  }) => {
    setDialogOpen(true);
    setLabInfo((prev) => ({
      ...prev,
      title,
      patient,
      ...(category && {
        category,
      }),
    }));
  };
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <>
      <TableContainer>
        {data.length > 0 ? (
          <MuiDataTable
            columns={[
              {
                name: 'viewed',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => {
                    return <TableStatus value={value} />;
                  },
                  searchable: false,
                  sort: false,
                  filter: false,
                  display: isAuthenticated,
                },
              },
              {
                name: 'curationStatus',
                label: 'Curation Status',
                className: 'small',
                options: {
                  customBodyRender: (status: CurationStatus) => (
                    <CurationStatusBadge status={status} />
                  ),
                  filter: isAdmin || isCurator,
                  filterType: 'multiselect',
                  display: isAdmin || isCurator,
                },
              },
              {
                name: 'recordSource',
                label: 'Record Source',
                className: 'small',
                options: {
                  customBodyRender: (source: ResourceSourceType) => (
                    <RecordSourcePill type={source} />
                  ),
                  filter: isVitall || isPractitioner,
                  filterType: 'multiselect',
                  display: isVitall || isPractitioner,
                },
              },
              {
                name: 'title',
                className: 'small',
                label: 'Type',
                options: {
                  filter: true,
                  searchable: true,
                  sort: true,
                  filterType: 'multiselect',
                },
              },
              {
                name: 'date',
                className: 'date',
                label: 'Date',
                options: {
                  filter: true,
                },
              },
              // {
              //   name: 'testName',
              //   className: 'small',
              //   label: 'Test Name',
              //   options: {
              //     filter: true,
              //     searchable: true,
              //     sort: true,
              //     filterType: 'multiselect',
              //   },
              // },
              {
                name: 'selfReported',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <SelfReportedPill value={value} />
                  ),
                  searchable: false,
                  sort: false,
                  filter: false,
                },
              },
            ]}
            data={tableData}
            options={{
              sortOrder: {
                name: 'date',
                direction: 'desc',
              },
              sort: true,
              search: true,
              filter: true,
              onRowClick: (_rowData, { dataIndex }) => {
                if (dataIndex <= tableData.length - 1) {
                  const row = tableData[dataIndex];
                  if (!isAuthenticated) {
                    handleOpenDialog({
                      title: row.title?.toString() ?? '',
                      patient: row.patient?.toString() ?? '',
                      category: row.type?.toString(),
                    });
                    return;
                  } else if (isCurator || isPractitioner) {
                    navigateRoute(ROUTES.LAB_RESULTS_REPORT, { id: row.id });
                  } else {
                    navigateRoute(ROUTES.LAB_RESULTS_REPORT, {
                      category: row.type,
                    });
                  }
                }
              },
            }}
            pageParam="labs"
          />
        ) : (
          <NullStateDiv>{formatMessage(messages.noRecords)}</NullStateDiv>
        )}
      </TableContainer>

      <Dialog
        content={
          <LabResultsDrilldown
            patient={labInfo.patient}
            title={labInfo.title}
            type={labInfo.category}
          />
        }
        onClose={handleCloseDialog}
        open={dialogOpen}
        title={labInfo.title}
      />
    </>
  );
};

export default LabResultListView;
